import BoardGame from './board-game.js';

/**
 * Location class
 * Represents a physical location with city, state, and distance.
 */
class Location {
    constructor(city, state, distance = Infinity) {
        this.city = city;
        this.state = state;
        this.distance = distance;
    }

    /**
     * Get formatted location string
     * @returns {string}
     */
    toString() {
        if (this.city && this.state) {
            return `${this.city}, ${this.state}`;
        }
        return this.city || this.state || '';
    }
}

/**
 * DealScore class
 * Represents the deal analysis for a listing.
 */
class DealScore {
    constructor(averagePrice = 0, score = '', includesExtras = false, allPriced = true) {
        this.averagePrice = averagePrice;
        this.score = score;
        this.includesExtras = includesExtras;
        this.allPriced = allPriced;
    }

    /**
     * Check if deal score has been calculated
     * @returns {boolean}
     */
    isCalculated() {
        return this.score !== '' && this.score !== null;
    }
}

/**
 * GeeklistItem class
 * 
 * Represents a single item in a BGG geeklist.
 * Contains the game info, pricing, seller, and calculated fields.
 */
class GeeklistItem {
    constructor(data = {}) {
        // Core identifiers
        this.id = data.id;
        this.href = data.href;
        this.link = data.link;
        
        // Game info
        this.item = data.item instanceof BoardGame 
            ? data.item 
            : BoardGame.fromApiResponse(data || {});
        
        // Content
        this.body = data.body || '';
        
        // Author/Seller info
        this.author = data.author;
        this.username = data.username;
        this.city = data.city;
        this.state = data.state;
        
        // Dates
        this.postdate = data.postdate instanceof Date 
            ? data.postdate 
            : (data.postdate ? new Date(data.postdate) : null);
        this.editdate = data.editdate instanceof Date 
            ? data.editdate 
            : (data.editdate ? new Date(data.editdate) : null);
        this.endDate = data.endDate instanceof Date 
            ? data.endDate 
            : (data.endDate ? new Date(data.endDate) : null);
        
        // Pricing
        this.price = data.price;
        this.priceType = data.priceType;
        
        // Status
        this.sold = data.sold || 'False';
        this.selected = data.selected || 'False';
        this.expansion = data.expansion || false;
        
        // Extras and matches
        this.extrasIds = data.extrasIds || [];
        this.matches = data.matches || 0;
        
        // Location
        this.location = data.location instanceof Location 
            ? data.location 
            : new Location(data.city, data.state, data.distance);
        
        // Deal score
        this.dealScore = data.dealScore instanceof DealScore 
            ? data.dealScore 
            : new DealScore(
                data.dealScore?.averagePrice || 0,
                data.dealScore?.score || '',
                data.dealScore?.includesExtras || false,
                data.dealScore?.allPriced ?? true
            );
        
        // Stats (from BGG)
        this.stats = data.stats || {};
        
        // Sale items (if sold via marketplace)
        this.saleitems = data.saleitems || [];
    }

    /**
     * Check if the item is marked as sold
     * @returns {boolean}
     */
    isSold() {
        return this.sold === 'True';
    }

    /**
     * Check if the item is selected
     * @returns {boolean}
     */
    isSelected() {
        return this.selected === 'True';
    }

    /**
     * Check if this is an expansion
     * @returns {boolean}
     */
    isExpansion() {
        return this.expansion === true;
    }

    /**
     * Check if this listing has extras
     * @returns {boolean}
     */
    hasExtras() {
        return this.extrasIds.length > 0;
    }

    /**
     * Get the game name
     * @returns {string}
     */
    getGameName() {
        return this.item?.name || '';
    }

    /**
     * Get the BGG URL for this item
     * @returns {string}
     */
    getBggUrl() {
        return this.link || `https://boardgamegeek.com${this.href}`;
    }

    /**
     * Create a GeeklistItem from raw API data with processing
     * @param {Object} data - Raw API data
     * @param {Object} user - User data (optional)
     * @param {Object} helpers - Helper functions (findPriceAndType, extractExtras, findSold, findAuctionEndDate)
     * @returns {GeeklistItem}
     */
    static fromApiResponse(data, user = null, helpers = {}) {
        const { findPriceAndType, extractExtras, findSold, findAuctionEndDate, BGG_BASE_URL } = helpers;
        
        const postdate = data.postdate ? new Date(data.postdate) : null;
        const priceAndType = findPriceAndType ? findPriceAndType(data.body) : { price: '?', type: 'N/A' };
        const extrasIds = extractExtras ? extractExtras(data.body).filter(extraId => extraId.toString() !== data.item?.id) : [];
        const endDate = findAuctionEndDate && postdate ? findAuctionEndDate(data.body, postdate) : null;
        
        const itemData = {
            ...data,
            postdate,
            editdate: data.editdate ? new Date(data.editdate) : null,
            endDate,
            price: priceAndType.price,
            priceType: priceAndType.type,
            sold: Boolean((data.saleitems && data.saleitems.length > 0) || (findSold && findSold(data.body))) ? 'True' : 'False',
            selected: 'False',
            expansion: /\bboardgameexpansion\b/i.test(data.item?.href || ''),
            link: BGG_BASE_URL ? BGG_BASE_URL.concat(data.href) : `https://boardgamegeek.com${data.href}`,
            dealScore: { averagePrice: 0, score: '' },
            extrasIds,
            location: new Location(user?.city || data.city, user?.state || data.state, Infinity)
        };

        if (user) {
            itemData.username = user.username;
            itemData.city = user.city;
            itemData.state = user.state;
        }

        return new GeeklistItem(itemData);
    }
}

// Export classes
export { Location, DealScore };
export default GeeklistItem;